import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import MainSidebar from '../../components/ui/MainSidebar';
import { emailScheduleService } from '../../services/emailScheduleService';
import ScheduleCard from './components/ScheduleCard';
import ScheduleForm from './components/ScheduleForm';
import RecipientManager from './components/RecipientManager';
import EmailLogsPanel from './components/EmailLogsPanel';

const EmailManagement = () => {
  const [schedules, setSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);

  useEffect(() => {
    loadSchedules();
  }, []);

  const loadSchedules = async () => {
    try {
      setLoading(true);
      const data = await emailScheduleService?.getAllSchedules();
      setSchedules(data || []);
    } catch (error) {
      console.error('Error loading schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSchedule = () => {
    setSelectedSchedule(null);
    setShowForm(true);
  };

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setShowForm(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;
    
    try {
      await emailScheduleService?.deleteSchedule(scheduleId);
      await loadSchedules();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      alert('Failed to delete schedule');
    }
  };

  const handleManageRecipients = (schedule) => {
    setSelectedSchedule(schedule);
    setShowRecipients(true);
  };

  const handleSendTest = async (scheduleId) => {
    try {
      setSending(scheduleId);
      await emailScheduleService?.sendTestEmail(scheduleId);
      alert('Test email sent successfully!');
    } catch (error) {
      console.error('Error sending test email:', error);
      alert('Failed to send test email: ' + error?.message);
    } finally {
      setSending(null);
    }
  };

  const handleFormClose = async (shouldRefresh) => {
    setShowForm(false);
    setSelectedSchedule(null);
    if (shouldRefresh) {
      await loadSchedules();
    }
  };

  const handleRecipientsClose = async (shouldRefresh) => {
    setShowRecipients(false);
    setSelectedSchedule(null);
    if (shouldRefresh) {
      await loadSchedules();
    }
  };

  const getReportTypeLabel = (type) => {
    const labels = {
      financial_report: 'Financial Report',
      client_analytics: 'Client Analytics',
      threshold_alerts: 'Threshold Alerts'
    };
    return labels?.[type] || type;
  };

  const getFrequencyLabel = (frequency) => {
    const labels = {
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly'
    };
    return labels?.[frequency] || frequency;
  };

  return (
    <div className="flex h-screen bg-background">
      <MainSidebar />
      <div className="flex-1 overflow-auto ml-64">
        <div className="p-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                Email Management
              </h1>
              <p className="text-muted-foreground">
                Configure scheduled reports and manage recipients
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogs(true)}
                className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth press-effect focus-ring"
              >
                <Icon name="History" size={20} />
                <span>Email Logs</span>
              </button>
              <button
                onClick={handleCreateSchedule}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth press-effect focus-ring"
              >
                <Icon name="Plus" size={20} />
                <span>New Schedule</span>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading schedules...</p>
              </div>
            </div>
          ) : schedules?.length === 0 ? (
            <div className="text-center py-16">
              <Icon name="Mail" size={64} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">
                No Email Schedules Yet
              </h3>
              <p className="text-muted-foreground mb-6">
                Create your first scheduled report to automate email delivery
              </p>
              <button
                onClick={handleCreateSchedule}
                className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth press-effect focus-ring"
              >
                Create Schedule
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {schedules?.map((schedule) => (
                <ScheduleCard
                  key={schedule?.id}
                  schedule={schedule}
                  onEdit={() => handleEditSchedule(schedule)}
                  onDelete={() => handleDeleteSchedule(schedule?.id)}
                  onManageRecipients={() => handleManageRecipients(schedule)}
                  onSendTest={() => handleSendTest(schedule?.id)}
                  isSending={sending === schedule?.id}
                  getReportTypeLabel={getReportTypeLabel}
                  getFrequencyLabel={getFrequencyLabel}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <ScheduleForm
          schedule={selectedSchedule}
          onClose={handleFormClose}
        />
      )}

      {showRecipients && selectedSchedule && (
        <RecipientManager
          schedule={selectedSchedule}
          onClose={handleRecipientsClose}
        />
      )}

      {showLogs && (
        <EmailLogsPanel onClose={() => setShowLogs(false)} />
      )}
    </div>
  );
};

export default EmailManagement;